/** Popover bodies for the compact usage indicators (cost, headroom, account, freshness). */
import { formatAgo, MetricRows } from '@viko/transcript-ui'
import {
  type AccountRef,
  formatTokens,
  formatUsd,
  type Headroom,
  isStale,
  type SessionUsage,
  totalTokens,
} from '../lib/usage-accounting.js'

const SOURCE: Record<SessionUsage['charge']['kind'], string> = {
  reported: 'Provider-reported',
  estimate: 'Viko estimate (from tokens and your rates)',
  missing: 'Not reported',
}

export function CostDetails({
  usage,
  model,
  now,
}: {
  usage: SessionUsage
  model?: string
  now: number
}) {
  const t = usage.tokens
  const c = usage.charge
  return (
    <>
      <MetricRows
        rows={[
          [
            'Cost',
            c.kind === 'missing'
              ? 'Not reported'
              : `${c.kind === 'estimate' ? '~' : ''}${formatUsd(c.usd)}`,
          ],
          ['Source', SOURCE[c.kind]],
          ['Model', model],
          ['Input', t ? `${formatTokens(t.input)} tokens` : undefined],
          ['Output', t ? `${formatTokens(t.output)} tokens` : undefined],
          ['Cache read', t ? `${formatTokens(t.cacheRead)} tokens` : undefined],
          ['Cache write', t ? `${formatTokens(t.cacheWrite)} tokens` : undefined],
          [
            'Total',
            t
              ? `${formatTokens(totalTokens(t))} tokens${usage.partial ? ' (subagents only)' : ''}`
              : undefined,
          ],
          [
            'Subagents',
            usage.children.count > 0
              ? `${usage.children.count} · ${formatTokens(usage.children.tokens)} tokens${usage.children.includedInTotal ? ' (already in the total above)' : ''}`
              : undefined,
          ],
          [
            'Updated',
            usage.observedAt
              ? `${formatAgo(usage.observedAt, now)}${isStale(usage.observedAt, now) ? ' (may be out of date)' : ''}`
              : 'Nothing reported yet',
          ],
        ]}
      />
      <p className="vk-metricpop__note">
        Cost is the API-equivalent figure. On a subscription plan you are not billed per token;
        watch headroom instead. Subagent usage is shown separately and never added twice.
      </p>
    </>
  )
}

export function HeadroomDetails({
  headroom,
  account,
  now,
}: {
  headroom: Headroom | undefined
  account?: AccountRef
  now: number
}) {
  if (!headroom)
    return (
      <p className="vk-metricpop__note">
        The provider has not reported a usage window for this account yet.
      </p>
    )
  return (
    <>
      <MetricRows
        rows={[
          ['Window', headroom.window],
          ['Used', `${Math.round(headroom.usedPct)}%`],
          ['Remaining', `${Math.round(headroom.remainingPct)}%`],
          ['Resets', headroom.resetsAt ? new Date(headroom.resetsAt).toLocaleString() : 'Unknown'],
          ['Account', account?.label],
          [
            'Updated',
            `${formatAgo(headroom.observedAt, now)}${isStale(headroom.observedAt, now) ? ' (stale)' : ''}`,
          ],
        ]}
      />
      <p className="vk-metricpop__note">
        Subscription quota for the busiest provider window. It is a limit reading, not a charge.
      </p>
    </>
  )
}

export function AccountDetails({ account }: { account?: AccountRef }) {
  return (
    <>
      <MetricRows rows={[['Account', account ? account.label : 'Unknown']]} />
      <p className="vk-metricpop__note">
        {account
          ? account.inferred
            ? 'This run did not record its account; this is the only enabled account for the agent on its machine.'
            : 'The provider account this run signs in with.'
          : 'This run did not record which provider account it uses.'}
      </p>
    </>
  )
}

export function FreshnessDetails({ usage, now }: { usage: SessionUsage; now: number }) {
  return (
    <>
      <MetricRows
        rows={[
          [
            'Last reading',
            usage.observedAt ? new Date(usage.observedAt).toLocaleString() : 'None yet',
          ],
        ]}
      />
      <p className="vk-metricpop__note">
        The agent reports usage as it works.{' '}
        {isStale(usage.observedAt, now)
          ? 'This reading is more than 15 minutes old, so the figures may be out of date.'
          : 'This reading is recent.'}
      </p>
    </>
  )
}
